import { API_URL } from '../config/api.config';

const TIMEOUT_DURATION = 10000; // 10 secondes

export class ApiService {
    static async fetchWithAuth(endpoint, options = {}) {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Non authentifié');
        }

        const defaultOptions = {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        };

        return this.fetch(endpoint, { ...defaultOptions, ...options });
    }

    static async fetch(endpoint, options = {}) {
        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
            },
        };

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_DURATION);

            const response = await fetch(`${API_URL}${endpoint}`, {
                ...defaultOptions,
                ...options,
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);

            if (!response.ok) {
                try {
                    const errorData = await response.json();
                    // Si le backend renvoie un objet avec une propriété 'message'
                    if (errorData && errorData.message) {
                        throw new Error(errorData.message);
                    }
                } catch (parseError) {
                    // Si ce n'est pas du JSON, essayer de lire comme texte
                    const errorText = await response.text();
                    throw new Error(errorText || 'Erreur lors de la requête');
                }
                throw new Error('Erreur lors de la requête');
            }

            // Vérifier si la réponse est vide
            const text = await response.text();
            if (!text) {
                return null;
            }

            try {
                return JSON.parse(text);
            } catch (e) {
                console.error('Erreur de parsing JSON:', e);
                throw new Error('Réponse invalide du serveur');
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                throw new Error('La requête a expiré. Veuillez réessayer.');
            }
            if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet ou réessayer plus tard.');
            }
            throw error;
        }
    }

    // Auth
    static async login(credentials) {
        const response = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
        });

        const data = await response.json();
        if (response.ok) {
            localStorage.setItem('token', data.token);
            return data;
        }
        throw new Error(data.message || 'Erreur de connexion');
    }

    // Menu Items
    static async getMenuItems() {
        return this.fetch('/menu');
    }

    static async getMainMenu() {
        return this.fetch('/menu/title/main');
    }

    static async getAnnexMenus() {
        return this.fetch('/menu/annex');
    }

    static async getMenuItemsByTitle(title) {
        return this.fetch(`/menu/title/${title}`);
    }

    static async createMenuItem(item) {
        return this.fetchWithAuth('/menu', {
            method: 'POST',
            body: JSON.stringify(item),
        });
    }

    static async updateMenuItem(id, item) {
        return this.fetchWithAuth(`/menu/${id}`, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    }

    static async deleteMenuItem(id) {
        const response = await this.fetchWithAuth(`/menu/${id}`, {
            method: 'DELETE',
        });
        return response;
    }

    // Réservations
    static async getReservations() {
        return this.fetchWithAuth('/reservations');
    }

    static async getPendingReservations() {
        return this.fetchWithAuth('/reservations/pending');
    }

    static async createReservation(reservationData) {
        return this.fetchWithAuth('/reservations', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(reservationData)
        });
    }

    static async updateReservation(id, reservationData) {
        return this.fetchWithAuth(`/reservations/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(reservationData)
        });
    }

    static async updateReservationStatus(id, status) {
        return this.fetchWithAuth(`/reservations/${id}/status?status=${status}`, {
            method: 'PATCH'
        });
    }

    static async deleteReservation(id) {
        return this.fetchWithAuth(`/reservations/${id}`, {
            method: 'DELETE',
        });
    }

    // Galerie
    static async getGalleryItems() {
        return this.fetch('/gallery/public');
    }

    static async getGalleryItemById(id) {
        return this.fetch(`/gallery/public/${id}`);
    }

    static async createGalleryItem(item) {
        return this.fetchWithAuth('/gallery', {
            method: 'POST',
            body: JSON.stringify(item),
        });
    }

    static async updateGalleryItem(id, item) {
        return this.fetchWithAuth(`/gallery/${id}`, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
    }

    static async deleteGalleryItem(id) {
        return this.fetchWithAuth(`/gallery/${id}`, {
            method: 'DELETE',
        });
    }

    static async toggleGalleryItemStatus(id) {
        return this.fetchWithAuth(`/gallery/${id}/toggle`, {
            method: 'PATCH',
        });
    }

    static async uploadGalleryImage(file) {
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Non authentifié');
        }

        const response = await fetch(`${API_URL}/gallery/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error || 'Erreur lors du téléchargement de l\'image');
        }

        return response.json();
    }

    static async uploadMenuImage(file, menuTitle) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('menuTitle', menuTitle);

        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Non authentifié');
        }

        const response = await fetch(`${API_URL}/menu/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error || 'Erreur lors du téléchargement de l\'image');
        }

        return response.json();
    }

    static async getAllUnavailableDates() {
        const response = await fetch(`${API_URL}/unavailable-dates/all`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
        });
        if (!response.ok) {
            throw new Error('Erreur lors de la récupération de toutes les dates non disponibles');
        }
        return await response.json();
    }

    static async addUnavailableDate(dateData) {
        return this.fetchWithAuth('/unavailable-dates', {
            method: 'POST',
            body: JSON.stringify(dateData)
        });
    }

    static async removeUnavailableDate(id) {
        const response = await fetch(`${API_URL}/unavailable-dates/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
        });
        if (!response.ok) {
            throw new Error('Erreur lors de la suppression de la date non disponible');
        }
        return true;
    }

    static async verifyToken() {
        const token = localStorage.getItem('token');
        if (!token) {
            return false;
        }

        try {
            const response = await fetch(`${API_URL}/reservations`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            return response.ok;
        } catch (error) {
            console.error('Erreur de vérification du token:', error);
            return false;
        }
    }
}
